import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './GuidePage.css';
import Navigation from './Navigation';
import { usePageTracking } from './hooks/useAnalytics';

function MeasureAccuracyPage() {
  usePageTracking('measure_accuracy_guide', {
    page_title: 'How to Measure Watch Accuracy at Home'
  });

  return (
    <div className="guide-page">
      <Helmet>
        <title>How to Measure Watch Accuracy at Home | ChronoLog</title>
        <meta name="description" content="Measure watch accuracy at home with no equipment: compare your watch to atomic time twice, 24 hours apart. Three methods, step by step, plus pitfalls." />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="How to Measure Watch Accuracy at Home" />
        <meta property="og:description" content="Measure watch accuracy at home with no equipment: compare your watch to atomic time twice, 24 hours apart. Three methods, step by step, plus pitfalls." />
        <meta property="og:url" content="https://www.chronolog.watch/measure-watch-accuracy" />
        <meta property="og:image" content="https://www.chronolog.watch/og-image.png" />
        <link rel="canonical" href="https://www.chronolog.watch/measure-watch-accuracy" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "headline": "How to Measure Watch Accuracy at Home",
                "description": "How to measure a watch's accuracy at home with no equipment: compare it to atomic time twice, hours apart, and scale the difference to seconds per day. Covers the manual method, timestamp apps, and audio timegraphers.",
                "author": { "@type": "Organization", "name": "ChronoLog", "url": "https://www.chronolog.watch" },
                "publisher": { "@type": "Organization", "name": "ChronoLog", "url": "https://www.chronolog.watch" },
                "url": "https://www.chronolog.watch/measure-watch-accuracy",
                "datePublished": "2026-10-12",
                "keywords": "how to measure watch accuracy, test watch accuracy at home, check if watch is running fast, measure watch accuracy without timegrapher, watch daily rate"
              },
              {
                "@type": "HowTo",
                "name": "Measure a watch's daily rate against atomic time",
                "description": "Determine how many seconds per day a watch gains or loses using only a trusted reference clock and two readings taken at least 24 hours apart.",
                "step": [
                  {
                    "@type": "HowToStep",
                    "name": "Open a trusted reference clock",
                    "text": "Open time.gov (or another atomic-time reference) on a computer or phone. A smartphone clock is also acceptable: it syncs to network time and typically stays within a fraction of a second of true time."
                  },
                  {
                    "@type": "HowToStep",
                    "name": "Take the first reading",
                    "text": "At the exact moment the reference ticks over to a full minute, read your watch's seconds hand and note how many seconds the watch is ahead of or behind the reference."
                  },
                  {
                    "@type": "HowToStep",
                    "name": "Record the offset and time",
                    "text": "Write down the offset (for example, 'watch is 4 seconds ahead') along with the date and time of the reading."
                  },
                  {
                    "@type": "HowToStep",
                    "name": "Wait at least 24 hours",
                    "text": "Leave the watch running normally. Do not reset the time, stop the watch, or let it wind down during the measurement window."
                  },
                  {
                    "@type": "HowToStep",
                    "name": "Take the second reading",
                    "text": "Repeat the reading against the same reference, again at the top of a reference minute, and note the new offset."
                  },
                  {
                    "@type": "HowToStep",
                    "name": "Calculate the daily rate",
                    "text": "Subtract the first offset from the second, then divide by the number of days elapsed. The result is the watch's rate in seconds per day; positive means it is running fast, negative means slow."
                  }
                ]
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "How do I check my watch's accuracy without equipment?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Compare your watch to a trusted reference clock such as time.gov twice, at least 24 hours apart, without resetting the watch in between. Note how many seconds the watch is ahead or behind at each reading, then divide the change by the days elapsed. The result is the watch's daily rate in seconds per day. All you need is the reference clock and a note of two readings."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "How long should I measure to know my watch's daily rate?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Use at least 24 hours between readings; shorter intervals magnify reading error. With roughly half a second of combined reading error, a 24-hour interval resolves the rate to about half a second per day, while a 6-hour interval blurs it to about 2 seconds per day. For a number you can trust, measure over several days or a full week, since a mechanical watch's rate varies with position and winding state."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What reference time should I set my watch against?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Use an atomic-time source: time.gov (run by NIST) is the standard choice in the US, and a smartphone's clock is usually fine because it syncs to network time and stays within a fraction of a second of true time. Avoid wall clocks, ovens, car dashboards, and older computers, which can drift by seconds per week. Whatever you choose, use the same reference for both readings so any constant offset cancels out."
                    }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <Navigation showAndroidButton={false} />

      <article className="article">
        <h1>How to Measure Watch Accuracy at Home</h1>
        <div className="article-meta">October 2026</div>

        <p className="article-lead">
          Measuring a watch's accuracy requires no equipment. Compare the watch against a trusted reference time, note the difference, then compare again at least 24 hours later. The change in that difference, divided by the days elapsed, is your watch's daily rate in seconds per day. Every tool that exists for this — apps, timegraphers, timing machines — is just a faster or more precise way of doing that one comparison.
        </p>

        <h2>The principle: two comparisons, hours apart</h2>

        <p>
          A watch's <strong>rate</strong> is how much it gains or loses per day, written as seconds per day (s/day). A watch running +5 s/day gains five seconds every 24 hours; one at &minus;3 s/day loses three. A single comparison against a reference clock tells you nothing about rate — it only tells you the watch was set imprecisely at some point. You need two comparisons separated by time, because rate is the <em>change</em> in offset, not the offset itself.
        </p>

        <p>
          That's the whole method. Everything below is about doing those two comparisons carefully enough that the number you get reflects the watch and not your measurement technique. For context on what number you should be hoping for, see <Link to="/accuracy">how accurate a mechanical watch should be</Link> — the short version is that a few seconds per day either way is normal for most mechanical movements.
        </p>

        <h2>Method 1: your watch vs. atomic time, by hand</h2>

        <p>
          This is the free, no-download method, and done carefully it resolves a watch's rate to better than a second per day.
        </p>

        <p>
          First, pick a reference. The gold standard in the US is <a href="https://www.time.gov" target="_blank" rel="noopener noreferrer">time.gov</a>, the official NIST/USNO time display. A smartphone's own clock is also acceptable: phones sync to network time and typically sit within a fraction of a second of true time. What you must avoid is anything that free-runs — wall clocks, microwave ovens, car dashboards, older desktop computers. These can drift by seconds per week, and if your reference drifts, that drift gets silently added to your watch's measured rate.
        </p>

        <p>
          Then take the first reading. At the exact moment the reference ticks over to a full minute, look at your watch's seconds hand and note the offset — "watch is 4 seconds ahead," for example. Reading at the top of a reference minute makes the comparison easier, because you only have to judge one moving hand instead of two. Write the offset down with the date and time. Then leave the watch alone: don't reset it, don't let it stop, and if it's a hand-wound watch, keep it wound as you normally would.
        </p>

        <p>
          At least 24 hours later, repeat the reading against the same reference. Subtract the first offset from the second and divide by the days elapsed. If the watch was +4 seconds yesterday and is +10 seconds today, it is running at +6 s/day.
        </p>

        <h3>The two pitfalls</h3>

        <p>
          <strong>Reference drift</strong> is the first, covered above: use atomic or network time, and use the same reference for both readings so any constant offset cancels out.
        </p>

        <p>
          <strong>Reading error</strong> is the second, and it's why the 24-hour minimum matters. Judging a seconds hand against a reference is good to maybe a quarter or half second per reading, and you take two readings. That uncertainty is fixed — but the rate you compute divides it by the elapsed time, so the longer you wait, the less it matters:
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Time between readings</th>
                <th>Combined reading error of &plusmn;0.5 s becomes&hellip;</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>6 hours</td>
                <td>&plusmn;2 s/day of rate uncertainty</td>
              </tr>
              <tr>
                <td>12 hours</td>
                <td>&plusmn;1 s/day</td>
              </tr>
              <tr>
                <td>24 hours</td>
                <td>&plusmn;0.5 s/day</td>
              </tr>
              <tr>
                <td>48 hours</td>
                <td>&plusmn;0.25 s/day</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          So a quick before-work and after-work check can't distinguish a +1 s/day watch from a +4 s/day watch. A full day between readings can.
        </p>

        <h2>Method 2: an app that timestamps for you</h2>

        <p>
          The manual method's weak point is the human: eyeballing a seconds hand against a ticking reference, twice, and keeping notes. An app can replace the eyeball with a timestamp. ChronoLog ($5.99 on iOS, Android in beta) does this with a quick-tap mode — you tap the moment your seconds hand hits a marker, and the tap is timestamped against atomic time — and a photo mode that reads the watch from a picture. Because each measurement is logged, the app accumulates a rate history per watch over days and weeks, which is exactly the repeated-measurement picture that a single manual check can't give you.
        </p>

        <p>
          The principle is unchanged: it's still two (or many) comparisons against a reference, hours apart. The app just shrinks the reading error and does the bookkeeping.
        </p>

        <h2>Method 3: listening to the movement</h2>

        <p>
          For mechanical watches there's a third approach that doesn't wait 24 hours: measure the beat directly. A mechanical movement ticks at a fixed design frequency — commonly 28,800 beats per hour — and a <Link to="/what-is-a-timegrapher">timegrapher</Link> measures how far the actual beat spacing deviates from that ideal, converting it to seconds per day in moments rather than days. Watchmakers use dedicated machines with contact sensors; a phone's microphone can do the same job through the air, with typical accuracy of &plusmn;2&ndash;5 s/day and no extra hardware. If you're curious how a phone mic pulls a tick out of background noise, the <Link to="/timegrapher">audio timegrapher deep dive</Link> walks through the signal processing.
        </p>

        <p>
          The trade-off: an acoustic reading is a snapshot of the watch in one position, at one state of wind, on a table. It's superb for diagnosis — is this watch wildly out, does it have a <Link to="/beat-error">beat error</Link>, does the rate swing between positions — but the number that matters for daily wear is the averaged rate on your wrist, which only the two-readings-over-time methods capture. The two approaches answer different questions, and they complement each other.
        </p>

        <h2>How long to measure for a trustworthy number</h2>

        <p>
          One 24-hour measurement gives you a real number, but a mechanical watch doesn't have a single fixed rate. It runs differently dial-up on a nightstand than crown-down in a drawer, differently fully wound than half wound, and slightly differently warm on a wrist than cool on a desk. Any single day's figure folds all of that into one number.
        </p>

        <p>
          A practical schedule: take a reading at the same time each day for five to seven days while wearing the watch normally. The average over the week is your watch's real-world rate; the spread between the best and worst day tells you how position-sensitive the movement is. If the daily figures cluster within a couple of seconds of each other, you can trust the average. If they swing widely, that variation is itself information — see the next section.
        </p>

        <h2>Reading your results: fast, slow, and position</h2>

        <p>
          <strong>Consistently fast or slow by a few seconds a day</strong> is the normal case. A steady +4 s/day is a healthy watch; whether it's worth regulating depends on the movement's spec and your tolerance, not on the sign of the number.
        </p>

        <p>
          <strong>Different rates in different positions</strong> is also normal, within reason. Gravity loads the balance pivots differently dial-up versus crown-down, so resting position changes the rate. You can use this: if your watch runs fast on the wrist, find an overnight resting position that runs slow, and the two partially cancel. Test it by measuring 24-hour rates with the watch left in one position overnight each night — dial up, dial down, crown down — and comparing.
        </p>

        <p>
          <strong>A sudden change</strong> — a watch that was +3 s/day for months and is now +40 — is a symptom, not a personality shift. Magnetization is the most common culprit for sudden gains; a fading mainspring or thickening oils show up as a gradual drift. <Link to="/watch-running-fast">Why a watch runs fast or slow</Link> covers how to tell the cases apart and what's fixable at home.
        </p>

        <div className="article-cta">
          <h2>Track your watch's rate automatically</h2>
          <p>
            ChronoLog timestamps your measurements against atomic time, builds a rate history for every watch in your collection, and includes an audio timegrapher — $5.99 one-time on the <a href="https://apps.apple.com/us/app/chronolog-watch-accuracy/id6452083510">App Store</a>.
          </p>
        </div>

        <div className="related-guides">
          <h2>Related guides</h2>
          <ul>
            <li><Link to="/accuracy">How accurate should a mechanical watch be?</Link></li>
            <li><Link to="/what-is-a-timegrapher">What is a timegrapher? Readings explained</Link></li>
            <li><Link to="/watch-running-fast">Why is my watch running fast or slow?</Link></li>
            <li><Link to="/timegrapher">Building a timegrapher from a phone microphone</Link></li>
          </ul>
        </div>
      </article>
    </div>
  );
}

export default MeasureAccuracyPage;
